// pages/formularios/SeletorModelo.js — "Modelo", o primeiro passo do construtor.
//
// Out/26. Os mesmos modelos do "Criar flow" da Meta, nas mesmas duas abas:
// "Simples" (o "sem ponto de extremidade" de lá) e "Conectados à Cetto"
// (o "com ponto de extremidade"; bloqueados até a fase própria). O nome
// técnico da Meta não diz nada pra quem monta, então a aba diz o que muda
// pro cliente.
//
// Cada cartão leva a miniatura da primeira tela, desenhada pela mesma
// prévia do construtor; passar o mouse (ou o foco) num cartão mostra o
// modelo inteiro na prévia grande ao lado (`onEspiar`). Depois de escolher,
// a seção vira uma linha só, com "Trocar modelo".

import { useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { FiCheck, FiLock, FiRepeat } from 'react-icons/fi';
import PreviaFlow from './PreviaFlow';
import { MODELOS_CONECTADOS, MODELOS_SIMPLES } from './modelos';
import { resumo } from './regras';
import { SYNE, ajuda, botaoTexto, rotuloSecao, selo } from './estilo';

function Cartao({ m, ativo, onEscolher, onEspiar }) {
  const bloqueado = Boolean(m.bloqueado);
  const amostra = useMemo(() => m.definicao(), [m]);
  return (
    <Box
      component="button"
      type="button"
      role="radio"
      aria-checked={ativo}
      aria-disabled={bloqueado || undefined}
      aria-label={`${m.nome}${bloqueado ? ', em breve' : ''}`}
      onClick={() => { if (!bloqueado) onEscolher(m); }}
      onMouseEnter={() => onEspiar(m)}
      onMouseLeave={() => onEspiar(null)}
      onFocus={() => onEspiar(m)}
      onBlur={() => onEspiar(null)}
      className="fm-anima"
      sx={{
        position: 'relative', textAlign: 'left', p: 1, borderRadius: '12px', cursor: bloqueado ? 'not-allowed' : 'pointer',
        bgcolor: 'var(--fm-paper)', font: 'inherit', color: 'inherit',
        border: ativo ? '2px solid var(--fm-green)' : '1px solid var(--fm-bdr)',
        m: ativo ? 0 : '1px',
        transition: 'border-color .15s, transform .15s',
        '&:hover': bloqueado ? {} : { borderColor: ativo ? 'var(--fm-green)' : 'var(--fm-bdr-h)', transform: 'translateY(-1px)' },
        '&:focus-visible': { outline: '2px solid var(--fm-focus)', outlineOffset: 2 },
      }}
    >
      <Box sx={{ opacity: bloqueado ? 0.55 : 1 }}>
        <PreviaFlow definicao={amostra} compacta />
      </Box>
      <Box sx={{ px: 0.5, pt: 1, pb: 0.4 }}>
        <Typography sx={{ fontFamily: SYNE, fontWeight: 700, fontSize: '0.84rem', color: 'var(--fm-title)', lineHeight: 1.25 }}>
          {m.nome}
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: 'var(--fm-body)', mt: 0.4, lineHeight: 1.35 }}>{m.resumo}</Typography>
        <Box sx={{ mt: 0.8, display: 'flex', gap: 0.6, alignItems: 'center', flexWrap: 'wrap' }}>
          {bloqueado
            ? <Box component="span" sx={selo('gray')}><FiLock size={10} /> Em breve</Box>
            : <Box component="span" sx={{ fontSize: '0.68rem', color: 'var(--fm-cap)', fontWeight: 600 }}>{resumo(amostra)}</Box>}
        </Box>
      </Box>
      {ativo && (
        <Box sx={{ position: 'absolute', top: 8, right: 8, width: 22, height: 22, borderRadius: '50%', bgcolor: 'var(--fm-green)', color: 'var(--fm-on-green)', display: 'grid', placeItems: 'center' }}>
          <FiCheck size={13} strokeWidth={3} />
        </Box>
      )}
    </Box>
  );
}

export default function SeletorModelo({ modelo, aberto, onEscolher, onAbrir, onEspiar, definicaoAtual }) {
  const [aba, setAba] = useState('simples');
  const escolhido = [...MODELOS_SIMPLES, ...MODELOS_CONECTADOS].find((m) => m.key === modelo);

  if (!aberto && escolhido) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', border: '1px solid var(--fm-bdr)', borderRadius: '12px', px: 2, py: 1.25, bgcolor: 'var(--fm-bg)' }}>
        <Typography sx={{ ...rotuloSecao, mr: 0.5 }}>Modelo</Typography>
        <Typography sx={{ fontWeight: 700, color: 'var(--fm-title)', fontSize: '0.9rem' }}>{escolhido.nome}</Typography>
        <Typography sx={{ fontSize: '0.78rem', color: 'var(--fm-body)' }}>{resumo(definicaoAtual)}</Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<FiRepeat size={13} />} onClick={onAbrir} sx={botaoTexto}>Trocar modelo</Button>
      </Box>
    );
  }

  const lista = aba === 'simples' ? MODELOS_SIMPLES : MODELOS_CONECTADOS;
  return (
    <Box>
      <Typography sx={rotuloSecao}>Modelo</Typography>
      <Typography sx={{ ...ajuda, mb: 1.5 }}>Comece por um dos modelos da Meta e ajuste à sua empresa.</Typography>

      <Box role="tablist" aria-label="Tipo de modelo" sx={{ display: 'inline-flex', p: '3px', gap: '2px', borderRadius: '10px', bgcolor: 'var(--fm-chip)', mb: 1 }}>
        {[['simples', 'Simples'], ['conectados', 'Conectados à Cetto']].map(([v, l]) => (
          <Box
            key={v}
            component="button"
            type="button"
            role="tab"
            aria-selected={aba === v}
            onClick={() => setAba(v)}
            sx={{
              border: 'none', cursor: 'pointer', font: 'inherit', px: 1.6, py: 0.7, borderRadius: '8px',
              fontFamily: SYNE, fontWeight: 700, fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 0.8,
              bgcolor: aba === v ? 'var(--fm-paper)' : 'transparent',
              color: aba === v ? 'var(--fm-title)' : 'var(--fm-body)',
              boxShadow: aba === v ? '0 1px 2px rgba(15,23,42,0.08)' : 'none',
              '&:focus-visible': { outline: '2px solid var(--fm-focus)', outlineOffset: 2 },
            }}
          >
            {v === 'conectados'
              ? <><Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{l}</Box><Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Conectados</Box></>
              : l}
            {v === 'conectados' && <Box component="span" sx={selo('gray')}>Em breve</Box>}
          </Box>
        ))}
      </Box>
      <Typography sx={{ ...ajuda, mb: 1.5 }}>
        {aba === 'simples'
          ? 'Tudo o que o cliente vê é definido aqui, na Cetto.'
          : 'A tela consulta a Cetto na hora em que o cliente abre, como os horários livres da Agenda. Chegam numa próxima etapa.'}
      </Typography>

      <Box role="radiogroup" aria-label="Modelos" sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: 'repeat(auto-fill, minmax(136px, 1fr))' }}>
        {lista.map((m) => (
          <Cartao key={m.key} m={m} ativo={m.key === modelo} onEscolher={onEscolher} onEspiar={onEspiar} />
        ))}
      </Box>
    </Box>
  );
}
